import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { adminAPI } from '@/utils/api';
import { toast } from 'sonner';
import { Sparkles, Shield, CheckCircle, XCircle, TrendingUp, Users, Store, Package, LogOut } from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [kpis, setKpis] = useState({
    gmv: 0,
    total_brands: 0,
    approved_brands: 0,
    total_products: 0,
    total_customers: 0,
    premium_members: 0
  });
  const [pendingBrands, setPendingBrands] = useState([]);
  const [pendingProducts, setPendingProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [kpisRes, brandsRes, productsRes] = await Promise.all([
        adminAPI.getKPIs(),
        adminAPI.getPendingBrands(),
        adminAPI.getPendingProducts()
      ]);
      setKpis(kpisRes.data);
      setPendingBrands(brandsRes.data);
      setPendingProducts(productsRes.data);
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveBrand = async (id) => {
    try {
      await adminAPI.approveBrand(id);
      toast.success('Brand approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve brand');
    }
  };

  const handleRejectBrand = async (id) => {
    try {
      await adminAPI.rejectBrand(id);
      toast.success('Brand rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject brand');
    }
  };

  const handleApproveProduct = async (id) => {
    try {
      await adminAPI.approveProduct(id);
      toast.success('Product approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve product');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A3A52]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* Navbar */}
      <nav className="border-b border-[#E5E1DC] bg-white/90 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Shield className="h-7 w-7 text-[#1A3A52]" strokeWidth={1.5} />
              <span className="text-2xl font-serif font-semibold text-[#2C3E50]">
                Admin Dashboard
              </span>
            </div>
            <Button
              data-testid="logout-btn"
              variant="ghost"
              onClick={handleLogout}
              className="text-[#5A6C7D] hover:text-[#1A3A52] hover:bg-[#F5F3F0]"
            >
              <LogOut className="h-5 w-5" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12">
        {/* Quick Actions */}
        <div className="mb-8">
          <Button
            data-testid="flash-sale-manager-btn"
            onClick={() => navigate('/admin/flash-sales')}
            className="bg-[#1A3A52] text-white hover:bg-[#2D4F6B] font-medium"
          >
            <Sparkles className="h-5 w-5 mr-2" strokeWidth={1.5} />
            Manage Flash Sales
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
          <div className="bg-white border border-[#E5E1DC] rounded-xl p-6 shadow-sm">
            <TrendingUp className="h-10 w-10 text-[#1A3A52] mb-3" strokeWidth={1.5} />
            <div className="text-3xl font-serif font-semibold mb-1 text-[#2C3E50]">${kpis.gmv.toLocaleString()}</div>
            <div className="text-[#5A6C7D] text-sm uppercase tracking-wide">Total GMV</div>
          </div>
          <div className="bg-white border border-[#E5E1DC] rounded-xl p-6 shadow-sm">
            <Store className="h-10 w-10 text-[#1A3A52] mb-3" strokeWidth={1.5} />
            <div className="text-3xl font-serif font-semibold mb-1 text-[#2C3E50]">{kpis.approved_brands}/{kpis.total_brands}</div>
            <div className="text-[#5A6C7D] text-sm uppercase tracking-wide">Approved Brands</div>
          </div>
          <div className="bg-white border border-[#E5E1DC] rounded-xl p-6 shadow-sm">
            <Package className="h-10 w-10 text-[#1A3A52] mb-3" strokeWidth={1.5} />
            <div className="text-3xl font-serif font-semibold mb-1 text-[#2C3E50]">{kpis.total_products}</div>
            <div className="text-[#5A6C7D] text-sm uppercase tracking-wide">Products</div>
          </div>
          <div className="bg-white border border-[#E5E1DC] rounded-xl p-6 shadow-sm">
            <Users className="h-10 w-10 text-[#1A3A52] mb-3" strokeWidth={1.5} />
            <div className="text-3xl font-serif font-semibold mb-1 text-[#2C3E50]">{kpis.premium_members}/{kpis.total_customers}</div>
            <div className="text-[#5A6C7D] text-sm uppercase tracking-wide">Premium Members</div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="brands" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 bg-white border border-[#E5E1DC]">
            <TabsTrigger value="brands" data-testid="brands-tab" className="text-[#5A6C7D] data-[state=active]:text-[#1A3A52] data-[state=active]:bg-[#F5F3F0]">Pending Brands ({pendingBrands.length})</TabsTrigger>
            <TabsTrigger value="products" data-testid="products-tab" className="text-[#5A6C7D] data-[state=active]:text-[#1A3A52] data-[state=active]:bg-[#F5F3F0]">Pending Products ({pendingProducts.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="brands" className="mt-8">
            {pendingBrands.length === 0 ? (
              <div className="text-center py-12 bg-white border border-[#E5E1DC] rounded-xl">
                <Store className="h-16 w-16 text-[#8B9AAC] mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-[#5A6C7D] text-lg">No pending brands</p>
              </div>
            ) : (
              <div className="space-y-6">
                {pendingBrands.map((brand) => (
                  <div
                    key={brand.id}
                    data-testid={`brand-${brand.id}`}
                    className="bg-white border border-[#E5E1DC] rounded-xl p-6 shadow-sm"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-2xl font-serif font-semibold mb-2 text-[#2C3E50]">{brand.brand_name}</h3>
                        <p className="text-[#5A6C7D] mb-4">{brand.description}</p>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-[#8B9AAC]">Owner ID:</span>
                            <div className="font-medium text-[#2C3E50]">{brand.owner_user_id}</div>
                          </div>
                          <div>
                            <span className="text-[#8B9AAC]">Discount Range:</span>
                            <div className="font-medium text-[#2C3E50]">{brand.discount_min}% - {brand.discount_max}%</div>
                          </div>
                          <div>
                            <span className="text-[#8B9AAC]">Commission:</span>
                            <div className="font-medium text-[#1A3A52]">{brand.commission_rate}%</div>
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-3 ml-6">
                        <Button
                          data-testid={`approve-brand-${brand.id}`}
                          onClick={() => handleApproveBrand(brand.id)}
                          className="bg-[#1A3A52] hover:bg-[#2D4F6B] text-white"
                        >
                          <CheckCircle className="h-5 w-5 mr-2" strokeWidth={1.5} />
                          Approve
                        </Button>
                        <Button
                          data-testid={`reject-brand-${brand.id}`}
                          onClick={() => handleRejectBrand(brand.id)}
                          variant="outline"
                          className="border-[#E5E1DC] text-[#5A6C7D] hover:bg-[#F5F3F0]"
                        >
                          <XCircle className="h-5 w-5 mr-2" strokeWidth={1.5} />
                          Reject
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="products" className="mt-8">
            {pendingProducts.length === 0 ? (
              <div className="text-center py-12 bg-white border border-[#E5E1DC] rounded-xl">
                <Package className="h-16 w-16 text-[#8B9AAC] mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-[#5A6C7D] text-lg">No pending products</p>
              </div>
            ) : (
              <div className="space-y-6">
                {pendingProducts.map((product) => (
                  <div
                    key={product.id}
                    data-testid={`product-${product.id}`}
                    className="bg-white border border-[#E5E1DC] rounded-xl p-6 flex items-start space-x-6 shadow-sm"
                  >
                    <img
                      src={product.images[0] || 'https://via.placeholder.com/120'}
                      alt={product.name}
                      className="w-32 h-32 rounded-lg object-cover"
                    />
                    <div className="flex-1">
                      <h3 className="text-2xl font-serif font-semibold mb-2 text-[#2C3E50]">{product.name}</h3>
                      <p className="text-[#5A6C7D] mb-4 line-clamp-2">{product.description}</p>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-[#8B9AAC]">MRP:</span>
                          <div className="font-medium text-[#2C3E50]">${product.mrp}</div>
                        </div>
                        <div>
                          <span className="text-[#8B9AAC]">Sale Price:</span>
                          <div className="font-medium text-[#1A3A52]">${product.current_price}</div>
                        </div>
                        <div>
                          <span className="text-[#8B9AAC]">Stock:</span>
                          <div className="font-medium text-[#2C3E50]">{product.stock}</div>
                        </div>
                        <div>
                          <span className="text-[#8B9AAC]">Category:</span>
                          <div className="font-medium text-[#2C3E50]">{product.category}</div>
                        </div>
                      </div>
                    </div>
                    <Button
                      data-testid={`approve-product-${product.id}`}
                      onClick={() => handleApproveProduct(product.id)}
                      className="bg-[#1A3A52] hover:bg-[#2D4F6B] text-white"
                    >
                      <CheckCircle className="h-5 w-5 mr-2" strokeWidth={1.5} />
                      Approve
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
